import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, VStack, HStack, Text, Spinner, Badge, Flex, IconButton, Button, NumberInput, NumberInputField,
    Switch, Table, Thead, Tbody, Tr, Th, Td, Wrap, WrapItem, useToast,
} from '@chakra-ui/react';
import { FiRefreshCw, FiDownload } from 'react-icons/fi';
import axios from 'axios';

const API_URL = process.env.REACT_APP_AWS_API_URL || '';

export default function BrandInsightsSection({ platformKey, onAuthError }) {
    const [minHouseholds, setMinHouseholds] = useState(10);
    const [minBrandHouseholds, setMinBrandHouseholds] = useState(3);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const toast = useToast();

    const headers = { 'x-platform-key': platformKey };
    const params = { minHouseholds, minBrandHouseholds };

    const handleError = (err, title) => {
        if (err?.response?.status === 401 || err?.response?.status === 403) onAuthError && onAuthError();
        else toast({ title, status: 'error', duration: 3000 });
    };

    const fetchReport = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/api/platform/brand-insights`, { params, headers });
            setReport(res.data);
        } catch (err) {
            handleError(err, 'Failed to load brand insights');
        } finally {
            setLoading(false);
        }
    }, [minHouseholds, minBrandHouseholds, platformKey]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { if (platformKey) fetchReport(); }, [platformKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleShop = async (shop) => {
        const enabled = !shop.shareInsights;
        if (enabled && !window.confirm(`Include ${shop.name}'s garment data (anonymized, by zip) in brand insights? Only do this once the shop has agreed.`)) return;
        try {
            await axios.post(`${API_URL}/api/platform/laundries/${shop.laundryId}/share-insights`, { enabled }, { headers });
            fetchReport();
        } catch (err) {
            handleError(err, 'Could not update shop');
        }
    };

    const downloadCsv = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/platform/brand-insights`, {
                params: { ...params, format: 'csv' }, headers, responseType: 'blob',
            });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'brand-insights-by-zip.csv';
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            handleError(err, 'Download failed');
        }
    };

    const zips = report?.zips || [];
    const shops = report?.shops || [];

    return (
        <VStack spacing={4} align="stretch">
            <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                <Text fontWeight="600" color="gray.700">Garment brands by zip (anonymized)</Text>
                <HStack>
                    <Button size="sm" leftIcon={<FiDownload />} variant="outline" onClick={downloadCsv} isDisabled={zips.length === 0}>
                        CSV
                    </Button>
                    <IconButton icon={<FiRefreshCw />} size="sm" variant="ghost" onClick={fetchReport} aria-label="Refresh" />
                </HStack>
            </Flex>

            <Text fontSize="xs" color="gray.500">
                Brands are read by AI from Advanced Tracking garment photos (visible logos only). Only shops switched on
                below are included. A zip is shown only when at least the minimum number of households were scanned
                there, and a brand only when enough households in that zip wear it, so no row identifies a household.
                Minimums can't go below 5 households per zip and 3 per brand.
            </Text>

            <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.100" p={3}>
                <Text fontSize="sm" fontWeight="600" mb={2}>Shops</Text>
                {shops.length === 0 ? (
                    <Text fontSize="sm" color="gray.400">No shops use Advanced Tracking yet.</Text>
                ) : (
                    <VStack align="stretch" spacing={2}>
                        {shops.map((s) => (
                            <HStack key={s.laundryId} justify="space-between">
                                <Box>
                                    <Text fontSize="sm" fontWeight="500">{s.name || s.laundryId}</Text>
                                    <Text fontSize="xs" color="gray.500">
                                        {s.garmentPhotos} garment photos · {s.brandedPhotos} with a brand
                                    </Text>
                                </Box>
                                <HStack>
                                    <Text fontSize="xs" color="gray.500">{s.shareInsights ? 'Included' : 'Not included'}</Text>
                                    <Switch isChecked={s.shareInsights} onChange={() => toggleShop(s)} colorScheme="teal" />
                                </HStack>
                            </HStack>
                        ))}
                    </VStack>
                )}
            </Box>

            <HStack spacing={4} wrap="wrap">
                <HStack>
                    <Text fontSize="sm">Min households per zip</Text>
                    <NumberInput size="sm" maxW="80px" min={5} value={minHouseholds}
                        onChange={(_, v) => setMinHouseholds(Number.isNaN(v) ? 5 : v)}>
                        <NumberInputField />
                    </NumberInput>
                </HStack>
                <HStack>
                    <Text fontSize="sm">Min households per brand</Text>
                    <NumberInput size="sm" maxW="80px" min={3} value={minBrandHouseholds}
                        onChange={(_, v) => setMinBrandHouseholds(Number.isNaN(v) ? 3 : v)}>
                        <NumberInputField />
                    </NumberInput>
                </HStack>
                <Button size="sm" colorScheme="teal" onClick={fetchReport}>Apply</Button>
            </HStack>

            {loading ? (
                <Flex justify="center" py={10}><Spinner size="lg" /></Flex>
            ) : zips.length === 0 ? (
                <Box textAlign="center" py={10} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.100">
                    <Text color="gray.400">No zip has enough scanned households yet.</Text>
                    {report?.suppressedZips > 0 && (
                        <Text fontSize="xs" color="gray.400" mt={1}>
                            {report.suppressedZips} zips ({report.suppressedHouseholds} households) are below the minimum.
                        </Text>
                    )}
                </Box>
            ) : (
                <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.100" overflowX="auto">
                    <Table size="sm" variant="simple">
                        <Thead>
                            <Tr>
                                <Th>Zip</Th>
                                <Th isNumeric>Households scanned</Th>
                                <Th>Top brands (share of households)</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {zips.map((z) => (
                                <Tr key={z.zip}>
                                    <Td fontFamily="mono">{z.zip}</Td>
                                    <Td isNumeric>{z.households}</Td>
                                    <Td>
                                        {z.brands.length === 0 ? (
                                            <Text fontSize="xs" color="gray.400">No brand reaches the minimum yet</Text>
                                        ) : (
                                            <Wrap spacing={1}>
                                                {z.brands.map((b) => (
                                                    <WrapItem key={b.brand}>
                                                        <Badge variant="subtle" colorScheme="purple" title={b.items.join(', ')}>
                                                            {b.brand} {b.share}%
                                                        </Badge>
                                                    </WrapItem>
                                                ))}
                                            </Wrap>
                                        )}
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                    {report?.suppressedZips > 0 && (
                        <Text fontSize="xs" color="gray.400" p={2}>
                            {report.suppressedZips} more zips ({report.suppressedHouseholds} households) hidden: below the minimum.
                        </Text>
                    )}
                </Box>
            )}
        </VStack>
    );
}
